import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useCutoffDay, showDate } from '../utils/months';

const styles = {
  h1: { fontSize: 22, fontWeight: 600, marginBottom: 20 },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 28 },
  card: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: '20px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  cardLabel: { fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  cardValue: { fontSize: 28, fontWeight: 700, marginTop: 4 },
  section: { marginBottom: 24 },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '10px 14px', backgroundColor: '#f8f9fa', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
  badge: (color) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: color === 'green' ? '#e6f9e6' : color === 'amber' ? '#fff8e6' : color === 'red' ? '#fee' : '#f0f0f0',
    color: color === 'green' ? '#27ae60' : color === 'amber' ? '#f39c12' : color === 'red' ? '#e74c3c' : '#666',
  }),
  link: { color: '#4a9eff', textDecoration: 'none' },
};

export default function Dashboard() {
  const cutoff = useCutoffDay(); // dates as months when sold by the month
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDashboard().then(setData).finally(() => setLoading(false));
  }, []);

  if (loading) return <div>Loading...</div>;
  if (!data) return <div>Failed to load dashboard</div>;

  return (
    <div>
      <h1 style={styles.h1}>Dashboard</h1>

      <div style={styles.cards}>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Active Subscribers</div>
          <div style={{ ...styles.cardValue, color: '#27ae60' }}>{data.total_active_subscribers}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Total Subscribers</div>
          <div style={styles.cardValue}>{data.total_subscribers}</div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Renewals Due (30 days)</div>
          <div style={{ ...styles.cardValue, color: data.upcoming_renewals_30d > 0 ? '#f39c12' : '#27ae60' }}>
            {data.upcoming_renewals_30d}
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardLabel}>Expiring This Week</div>
          <div style={{ ...styles.cardValue, color: data.expiring_this_week > 0 ? '#e74c3c' : '#27ae60' }}>
            {data.expiring_this_week}
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Subscribers by Title</div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Active</th>
              <th style={styles.th}>Pending Renewal</th>
              <th style={styles.th}>Expired</th>
            </tr>
          </thead>
          <tbody>
            {data.by_title.map(t => (
              <tr key={t.id}>
                <td style={styles.td}>
                  <Link to={`/titles/${t.id}`} style={styles.link}>{t.name}</Link>
                </td>
                <td style={styles.td}><span style={styles.badge('green')}>{t.active}</span></td>
                <td style={styles.td}><span style={styles.badge('amber')}>{t.pending_renewal}</span></td>
                <td style={styles.td}><span style={styles.badge('red')}>{t.expired}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Recent Subscriptions</div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Subscriber</th>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>{cutoff ? 'First issue' : 'Start Date'}</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_subscriptions.map(s => (
              <tr key={s.id}>
                <td style={styles.td}>{s.subscriber_name}</td>
                <td style={styles.td}>{s.title_name}</td>
                <td style={styles.td}>{showDate(s.start_date, cutoff)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
